import type { Footer as Props } from '@ecomm/footer-data'
import { getOneTrust } from '@ecomm/shared-window/lib/getOneTrust'
import classNames from 'classnames'
import React from 'react'
import { Copyright } from './Copyright'
import { CountrySelector } from './CountrySelector'
import { LegalLinks } from './LegalLinks'
import MenuAccordions from './MenuAccordions'
import OffersForm from './OffersForm'
import { SocialMediaLinks } from './SocialMediaLinks'

export type FooterProps = Props & {
  readonly type?: 'Full' | 'Slim'
  readonly showCountrySelector?: boolean
}

export const Footer = React.forwardRef<HTMLDivElement, FooterProps>(
  (
    {
      disclaimerLink,
      legalLinksCollection: { items: legalLinks },
      socialLinksCollection: { items: socialLinks },
      copyrightText,
      menusCollection: { items: menus },
      leadGenForm,
      type = 'Slim',
      showCountrySelector = true
    },
    ref
  ) => {
    const isSlim = type === 'Slim'
    // these classes are used to raise mobile footer content above the minimized mobile promo banner
    const paddingClasses = 'pb-10 lg:pb-0'
    return (
      <footer
        className={`prose md:prose-md lg:prose-lg whitespace-pre-line bg-neutral-black prose-headings:text-white prose-p:text-white prose-a:text-white text-white ${paddingClasses}`}
        data-component="Footer"
        data-testid="Footer"
        ref={ref}
      >
        <div className="max-w-8xl mx-auto px-4 py-6 md:p-8">
          <div
            className={classNames(
              'mb-4 flex justify-between md:flex-row md:flex-wrap md:items-start lg:!flex-nowrap lg:items-start',
              {
                'flex-col': !isSlim
              }
            )}
          >
            {leadGenForm ? (
              <OffersForm
                className={classNames(
                  'mb-6 md:!block md:basis-2/3	lg:basis-2/5',
                  {
                    hidden: isSlim
                  }
                )}
                leadForm={leadGenForm}
              />
            ) : null}
            {menus && menus.length > 0 ? (
              <MenuAccordions
                className={classNames(
                  'mb-6 md:order-last lg:!order-first lg:!mr-8 lg:!max-w-xl',
                  {
                    'basis-2/3 md:basis-1/3 lg:!basis-1/4 xl:!basis-1/4':
                      isSlim,
                    'md:flex md:basis-full md:justify-between lg:!basis-1/2 xl:!basis-1/2':
                      !isSlim
                  }
                )}
                menus={menus}
                type={type}
              />
            ) : null}
            {showCountrySelector ? <CountrySelector /> : null}
          </div>
          <div
            className={classNames(
              'flex items-baseline justify-between md:flex-row md:justify-between',
              {
                'flex-col': isSlim,
                'justify-between': !isSlim
              }
            )}
          >
            {copyrightText ? (
              <Copyright
                className={classNames('basis-1/3 md:text-center', {
                  'order-last': isSlim
                })}
                text={copyrightText}
              />
            ) : null}
            {socialLinks && socialLinks.length > 0 ? (
              <SocialMediaLinks
                className="basis-1/3 justify-end pl-0 md:order-first md:justify-start"
                links={socialLinks}
              />
            ) : null}
            {legalLinks && legalLinks.length > 0 ? (
              <LegalLinks
                className={classNames(
                  {
                    hidden: !isSlim
                  },
                  'basis-1/3 list-none justify-center md:order-last md:!flex md:justify-center md:gap-1'
                )}
                links={legalLinks}
              />
            ) : null}
          </div>

          {/* TODO: ECP-15041 - This is a temporary solution to display the disclaimer button.
           * Since we changed the <a> element to a <button> element, we need to update the
           * Contentful schema to handle a disclaimer text-only field.
           * https://simplisafe.atlassian.net/browse/ECP-15041
           */}
          {disclaimerLink?.text ? (
            <div>
              <button
                className="ot-sdk-show-settings cursor-pointer text-xs text-white no-underline hover:underline bg-transparent border-none p-0"
                onClick={() => {
                  getOneTrust(oneTrust => {
                    oneTrust?.ToggleInfoDisplay?.()
                  })
                }}
              >
                {disclaimerLink.text}
              </button>
            </div>
          ) : null}
        </div>
      </footer>
    )
  }
)
